import { LoadingButton } from "@mui/lab";
import { Alert, Grid, MenuItem, TextField } from "@mui/material";
import AutoCompleteList from "components/AutoCompleteList";
import SimpleBarScroll from "components/SimpleBar";
import { useFormik } from "formik";
import React, { useCallback, useEffect, useState } from "react";
import { useSelector } from "react-redux";
import { dispatch } from "store";
import moment from "moment";
import { AdapterMoment } from "@mui/x-date-pickers/AdapterMoment";
import { DesktopDatePicker, LocalizationProvider } from "@mui/x-date-pickers";
import * as yup from "yup";
import { assignProductThunk } from "store/reducers/refill/refillSlice";
import {
  getCategoryForMachineThunk,
  getProductForMachineThunk,
  getProductVariantForMachineThunk,
  getSubCategoryForMachineThunk,
} from "store/reducers/refill/productsForMachineSlice";
import { useParams } from "react-router-dom";

const RefillForm = ({ tray_id, qty, partition, fullGrid, handleClose }) => {
  const { m_id } = useParams();
  const [category, setCategory] = useState(null);
  const [subCategory, setSubCategory] = useState(null);
  const [product, setProduct] = useState(null);

  const state = useSelector((state) => state.productsForMachine);
  const categories = state.category;
  const subCategories = state.sub_category.filter(
    (item) => item.category === category?.id
  );

  const products = state.products.filter(
    (item) =>
      item.category === category?.id && item.sub_category === subCategory?.id
  );

  const variants = useSelector((state) => state.productsForMachine.variants).filter(
    (item) => item.product === product?.id
  );
  const validation = yup.object({
    qty: yup
      .number()
      .required("Please enter partition number")
      .positive()
      .min(1)
      .max(
        partition.max_count - partition.current_count > (variants?.qty ?? 0)
          ? partition.max_count - partition.current_count
          : variants?.qty
      ),
    expiry: yup.string().nullable(),
    index: yup.number().nullable(),
    product_variant: yup.number().required(),
  });
  const formik = useFormik({
    initialValues: {
      partition: partition.id,
      expiry: moment().add(7, "days").format("YYYY-MM-DD"),
      index: null,
      product_variant: "",
    },
    validationSchema: validation,
    onSubmit: (data) => {
      // console.log(data);
      dispatch(assignProductThunk({ tray: tray_id, data }));
    },
    validateOnChange: false,
    enableReinitialize: true
  });

  useEffect(() => {
    dispatch(getCategoryForMachineThunk(m_id));
  }, []);

  useEffect(() => {
    if (category) {
      setSubCategory(null);
      dispatch(
        getSubCategoryForMachineThunk({ machine: m_id, category: category.id })
      );
    }
  }, [category, m_id]);

  useEffect(() => {
    if (subCategory) {
      dispatch(
        getProductForMachineThunk({
          machine: m_id,
          sub_category: subCategory?.id ?? "",
        })
      );
    }
  }, [category, m_id, subCategory]);

  useEffect(() => {
    if (product) {
      dispatch(
        getProductVariantForMachineThunk({ machine: m_id, product: product.id })
      );
    }
  }, [m_id, product]);

  useEffect(() => {
    console.log("Variants", variants);
    if ((variants?.length ?? 0) > 0) {
      if (!formik.values.product_variant) {
        formik.setFieldValue("product_variant", variants[0]?.id ?? "");
      }
    }
  }, [variants]);

  const { loading, error, errorMessage, success } = useSelector(
    (state) => state.refill
  );
  useEffect(() => {
    if (errorMessage?.assign_product) {
      Object.keys(errorMessage?.assign_product).forEach((key) => {
        console.log(key, errorMessage?.assign_product[key][0]);
        formik.setFieldError(key, errorMessage?.assign_product[key][0]);
      });
    }
  }, [errorMessage]);

  const [maxProduct, setMaxProduct] = useState(
    partition.max_count - partition.current_count
  );

  const qtyFilter = useCallback(
    (variant_id, partition) => {
      const partMax = partition.max_count - partition.current_count;

      if (!variant_id) {
        return partMax;
      }

      const variant = variants.filter(
        (variant) => variant.id === variant_id
      )[0];

      if (partMax > (variant?.qty ?? 0)) {
        return variant?.qty ?? partMax;
      } else {
        return partMax;
      }
    },
    [variants]
  );

  useEffect(() => {
    setMaxProduct(qtyFilter(formik.values.product_variant, partition));
  }, [formik.values.product_variant, partition, qtyFilter]);

  useEffect(() => {
    if (success?.assign_product === true && handleClose) {
      handleClose();
    }
  }, [success]);

  return (
    <>
      <SimpleBarScroll>
        <form onSubmit={formik.handleSubmit}>
          <Grid container spacing={1.5} paddingY={1}>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <AutoCompleteList
                items={categories}
                name="name"
                label="Filter categories"
                setValue={setCategory}
                value={category}
              />
            </Grid>

            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <AutoCompleteList
                items={subCategories}
                name="name"
                label="Filter subcategories"
                setValue={setSubCategory}
                value={subCategory}
              />
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <AutoCompleteList
                items={products}
                name="name"
                label="Select product"
                setValue={setProduct}
                value={product}
              />
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label="Select price variant"
                variant="outlined"
                name="product_variant"
                select
                fullWidth
                value={formik.values.product_variant}
                onChange={formik.handleChange("product_variant")}
                error={Boolean(formik.errors.product_variant)}
                helperText={formik.errors.product_variant}
                autoComplete="new-password"
              >
                {variants.map((variant) => (
                  <MenuItem value={variant.id} key={variant.id}>
                    ₹{variant.price}
                    {variant.qty && ` [${variant.qty} Nos left]`}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <LocalizationProvider dateAdapter={AdapterMoment}>
                <DesktopDatePicker
                  label="Expiry date"
                  inputFormat="YYYY-MM-DD"
                  name="expiry"
                  value={formik.values.expiry}
                  onChange={(value) =>
                    formik.setFieldValue(
                      "expiry",
                      value.format("YYYY-MM-DD"),
                      true
                    )
                  }
                  renderInput={(params) => (
                    <TextField fullWidth variant="outlined" {...params} />
                  )}
                />
              </LocalizationProvider>
            </Grid>

            <Grid item xs={12} md={fullGrid ? 12 : 6} lg={fullGrid ? 12 : 4}>
              <TextField
                label={`Qty (max ${maxProduct})`}
                variant="outlined"
                name="qty"
                fullWidth
                value={formik.values.qty}
                onChange={formik.handleChange}
                error={Boolean(formik.errors.qty)}
                helperText={formik.errors.qty}
                autoComplete="new-password"
                type="number"
                required
              />
            </Grid>
          </Grid>

          {error?.assign_product && (
            <Alert sx={{ my: 1 }} severity="error">
              Error occurred
            </Alert>
          )}

          {success?.assign_product && (
            <Alert sx={{ my: 1 }} severity="success">
              Success
            </Alert>
          )}
          <LoadingButton
            loading={loading?.assign_product}
            variant="contained"
            type="submit"
            sx={{ paddingX: 10, marginTop: 1 }}
          >
            Fill product
          </LoadingButton>
        </form>
      </SimpleBarScroll>
    </>
  );
};

export default RefillForm;